import type { Characteristic } from '@shared/cards-types';
import type { CardsFeatureConfig } from '@shared/cards-config';

import {
    AccountLinkageFeatureConfig,
    AuthorizedCardHeaderDetailsFeatureConfig,
    CardActivationFeatureConfig,
    CardBankHeaderFeatureConfig,
    CardBlockingFeatureConfig,
    CardCancellationFeatureConfig,
    CardDeliveryFeatureConfig,
    CardLimitsChangeFeatureConfig,
    CardLimitsCombinedFeatureConfig,
    CardLockAndReplaceFeatureConfig,
    CardLockFeatureConfig,
    CardLoyaltyProgramExternalFeatureConfig,
    CardMigrationExternalFeatureConfig,
    CardsBankListFeatureConfig,
    CardsCreditCardFeesFeatureConfig,
    CardsCreditLimitChangeFeatureConfig,
    CardsCreditLimitInfoFeatureConfig,
    CardsSettingsCardDetailsFeatureConfig,
    CreditCardContentBoxFeatureConfig,
    CreditCardReissueExternalFeatureConfig,
    CreditCardRepaymentMethodChangeExternalFeatureConfig,
    CreditSharedFeatureConfig,
    DebitCardContentBoxFeatureConfig,
    DisplayPinFeatureConfig,
    InstallmentsActivationFeatureConfig,
    InstallmentsFeatureConfig,
    OrderReplacementCardFeatureConfig,
    PinDeliveryFeatureConfig,
    ReprintPinFeatureConfig,
    cardsCreditLimitChangeExternalFeatureConfig,
} from './featureConfig';

export const cards: CardsFeatureConfig = {
    isNativeAliasChangeAllowed: false,
    overview: {
        CREDIT: {
            MAIN: {
                hide: false,
                displayLimitInfo: true,
            },
            AUTHORIZED: {
                hide: false,
                displayLimitInfo: true,
            },
            AUTHORIZED_STANDALONE: {
                hide: false,
                displayLimitInfo: true,
            },
            displayLimit: false,
        },
        BANK_CARD: {
            MAIN: {
                hide: false,
            },
            AUTHORIZED: {
                hide: false,
            },
            AUTHORIZED_STANDALONE: {
                hide: false,
            },
            hide: false,
        },
        VIRTUAL_CREDIT: {
            MAIN: {
                hide: true,
                displayLimitInfo: true,
            },
            AUTHORIZED: {
                hide: true,
                displayLimitInfo: true,
            },
            AUTHORIZED_STANDALONE: {
                hide: true,
                displayLimitInfo: true,
            },
            displayLimit: false,
        },
        VIRTUAL_DEBIT: {
            MAIN: {
                hide: true,
            },
            AUTHORIZED: {
                hide: true,
            },
            AUTHORIZED_STANDALONE: {
                hide: true,
            },
        },
    },
    images: {
        DEFAULT: '/img/default-card.png',
        CREDIT: '/img/default-credit-card.png',
        BANK_CARD: '/img/default-bank-card.png',
    },
    bank: {
        removeCardAlias: false,
        showAsProduct: false,
        asProduct: false,
        hasInsurance: false,
        settings: {
            limitChange: true,
        },
        supplementary: {
            enabled: false,
            _description:
                'Enables loading of authorized cards into the search filter on the history page',
        },
        forceHidingOfLimitChange: false,
        showVirtualCardOrderSection: false,
        showSupplementaryCardBalance: false,
        hideOverlayOnCardImage: false,
        hideAliasInCardHeader: false,
        showValidityInCardHeader: false,
    },
    bank_card: {
        settings: {
            /* Display bank cards in the Customize George page */
            allowPersonalization: true,
            productWhitelist: [] as string[],
        },
        reservations: {
            hideDescriptionText: false,
            description: true,
        },
    },
    credit: CreditSharedFeatureConfig.base,
    shared: {
        menu: {
            separateAuthorized: true,
        },
        functions: {
            tpLimits: {
                /** Limits are displayed based on card features and flags */
                drivenByFeaturesAndFlags: false,
                version: 2,
                limitChangeVersion: 2,
                permanentAlwaysActive: false,
                payloadPrecision: 2,
            },
            limitChange: {
                displayInternetLimitFormField: false,
                displayATMLimitFormField: false,
                displayTotalLimitFormFieldOnly: false,
                forbidFreeFormLimitAmountInput: false,
            },
            lockAndReplace: {
                lockOnly: {
                    CREDIT: {
                        enabled: false,
                        _description:
                            'Enables lock only button for credit card on the lock and replace focus page',
                    },
                    BANK_CARD: {
                        enabled: false,
                        _description:
                            'Enables lock only button for bank card on the lock and replace focus page',
                    },
                },
                lockAndReplace: {
                    CREDIT: {
                        enabled: false,
                        _description:
                            'Enables lock and replace button for credit card on the lock and replace focus page',
                    },
                    BANK_CARD: {
                        enabled: false,
                        _description:
                            'Enables lock and replace button for bank card on the lock and replace focus page',
                    },
                },
            },
        },
        settings: {
            sos: {
                preferFlag: false,
                editable: false,
                loadDetails: false,
                showInfo: false,
            },
            reissue: {
                reorder: {
                    displayAsCard: false,
                },
                lock: {
                    doubleFetchEnabled: false,
                    confirmationLanguages: [] as string[],
                    doubleFetchTimeout: 0,
                    reasons: false,
                },
                pin: {
                    fees: {
                        enabled: false,
                        types: [] as string[],
                    },
                    deliveryModes: {
                        _description:
                            'Displays card delivery modes info in the reissue success modal view',
                        enabled: false,
                    },
                    deliveryAddress: {
                        enabled: false,
                        types: [] as string[],
                    },
                },
                unlock: {
                    doubleFetchEnabled: false,
                    doubleFetchTimeout: 0,
                },
            },
            showMainAccountForStandalone: false,
        },
    },
    components: {
        cardDelivery: CardDeliveryFeatureConfig.base,
        pinDelivery: PinDeliveryFeatureConfig.base,
        cardFees: CardsCreditCardFeesFeatureConfig.base,
        cardServices: {
            replacementCardV2: {
                enabled: false,
                emailConfirmation: false,
                forcePhonePinDelivery: false,
                deliveryManagement: false,
            },
            reprintPin: ReprintPinFeatureConfig.base,
            orderReplacementCard: OrderReplacementCardFeatureConfig.base,
        },
        cardServicesList: {
            displayPin: DisplayPinFeatureConfig.base,
        },
        settings: {
            accountLinkage: AccountLinkageFeatureConfig.base,
            cardDetails: CardsSettingsCardDetailsFeatureConfig.base,
        },
        limits: CardLimitsCombinedFeatureConfig.base,
        limitsChange: CardLimitsChangeFeatureConfig.base,
        installments: InstallmentsFeatureConfig.base,
        installmentsActivation: InstallmentsActivationFeatureConfig.base,
        cardLock: CardLockFeatureConfig.base,
        cardLockAndReplace: CardLockAndReplaceFeatureConfig.base,
        creditCardLimitChange: CardsCreditLimitChangeFeatureConfig.base,
        creditCardLimitInfo: CardsCreditLimitInfoFeatureConfig.base,
        cardsList: CardsBankListFeatureConfig.base,
        bankCardHeader: CardBankHeaderFeatureConfig.base,
        cardLoyaltyProgramExternal: CardLoyaltyProgramExternalFeatureConfig.base,
        cardMigrationExternal: CardMigrationExternalFeatureConfig.base,
        creditCardReissueExternal: CreditCardReissueExternalFeatureConfig.base,
        creditCardRepaymentMethodChangeExternal:
            CreditCardRepaymentMethodChangeExternalFeatureConfig.base,
        cardsCreditLimitChangeChangeExternal: cardsCreditLimitChangeExternalFeatureConfig.base,
        cardBlocking: CardBlockingFeatureConfig.base,
        cardActivation: CardActivationFeatureConfig.base,
        cardCancellation: CardCancellationFeatureConfig.base,
        authorizedCardHeaderDetails: AuthorizedCardHeaderDetailsFeatureConfig.base,
        creditCardContentBox: CreditCardContentBoxFeatureConfig.base,
        debitCardContentBox: DebitCardContentBoxFeatureConfig.base,
    },
    statements: {
        signed: false,
        onlyElectronic: false,
        showPhoneNumbersDropdown: true,
        reversibleToPaperDelivery: true,
        deliverySelectable: false,
        deliveryDefinitions: {
            distribution: ['GEORGE', 'GEORGE_AND_EMAIL'],
            format: ['PDF_A4'],
            periodicity: ['MONTHLY'],
            canCreateDefinition: false,
            channelEditable: true,
            statementNumberEditable: false,
        },
    },
    navigation: {
        excludeCharacteristics: [] as Characteristic[],
    },
};
